// Top-of-grid toolbar: search input, filter funnel button, sort/clear,
// full-screen toggle. Designed to live in a row above the grid; the
// filter chip bar (FilterChipBar) sits between this toolbar and the
// grid body so active filters are always visible.
//
// `enabled` toggles determine which controls render. When all features
// are disabled the toolbar collapses to null (parent renders nothing).

import {
  Maximize2, Minimize2, Search, SlidersHorizontal, X,
} from 'lucide-react'

import type { EmbedGridUx } from '@twasta/page-schema'
import type { SortSpec } from './gridTypes'
import { tw } from '../../testHooks'

interface Props {
  ux: EmbedGridUx
  search: string
  onSearchChange: (s: string) => void
  sort: SortSpec[]
  onClearSort: () => void
  isFullScreen: boolean
  onToggleFullScreen: () => void
  // Designer hooks
  totalsButton?: React.ReactNode
  // Slot for "+ Filter" button rendered by parent so it can host its
  // own column picker without bloating this component.
  addFilterButton?: React.ReactNode
  // Free-form info area on the right (record count, sync chip, etc.).
  rightSlot?: React.ReactNode
}

export default function GridToolbar({
  ux, search, onSearchChange, sort, onClearSort,
  isFullScreen, onToggleFullScreen, totalsButton, addFilterButton, rightSlot,
}: Props) {
  const showSearch = ux.search !== false
  const showFilter = ux.filter !== false
  const showSort = ux.sort !== false
  const showFullScreen = ux.fullScreen !== false

  if (!showSearch && !showFilter && !showSort && !showFullScreen
      && !totalsButton && !rightSlot) {
    return null
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-surface-2, var(--color-bg-secondary))',
      fontSize: '0.9125rem',
      flexWrap: 'wrap',
    }}>
      {showSearch && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 6, padding: '0 8px',
          height: 28, boxSizing: 'border-box',
          minWidth: 180,
        }}>
          <Search size={12} style={{ color: 'var(--color-text-secondary)' }} />
          <input
            type="search"
            data-grid-search
            {...tw('field', '_search')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search in table"
            style={{
              flex: 1,
              padding: '2px 4px',
              background: 'transparent',
              color: 'var(--color-text)',
              border: 0, outline: 'none',
              fontSize: '0.9125rem',
            }}
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              {...tw('action', 'clear_search')}
              className="btn btn-ghost btn-sm"
              style={{ padding: 2 }}
              title="Clear search"
            >
              <X size={10} />
            </button>
          )}
        </div>
      )}

      {showFilter && addFilterButton}

      {showSort && sort.length > 0 && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          color: 'var(--color-text-secondary)',
        }}>
          <SlidersHorizontal size={11} />
          Sort: {sort.map((s, i) => (
            <span key={s.column}>
              {i > 0 && ', '}
              {s.column} {s.dir === 'asc' ? '↑' : '↓'}
            </span>
          ))}
          <button
            onClick={onClearSort}
            className="btn btn-ghost btn-sm"
            style={{ padding: 2 }}
            title="Clear sort"
          >
            <X size={10} />
          </button>
        </span>
      )}

      {totalsButton}

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {rightSlot}
        {showFullScreen && (
          <button
            onClick={onToggleFullScreen}
            className="btn btn-ghost btn-sm"
            title={isFullScreen ? 'Exit full screen' : 'Full screen'}
            style={{
              padding: '0 6px', height: 28, boxSizing: 'border-box',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        )}
      </div>
    </div>
  )
}
